import React, { useState, useEffect } from 'react';
import { Modal, Form, Spinner, Alert } from 'react-bootstrap';
import CompactUpgradePrompt from '../../../Components/CompactUpgradePrompt';
import '../agent-training.css';
import { CancelButton, PrimaryButton } from '../../../Components/Buttons';
import { useLanguage } from '../../../Context/LanguageContext';

const MAX_LINKS = 10;

const AddLinksModal = ({
  show,
  onHide,
  workspaceId,
  apiUrl,
  onPollState,
  crawledUrlCount,
  crawlUrlLimit
}) => {
  const { t } = useLanguage();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [linkCountError, setLinkCountError] = useState(false);
  const [invalidLinks, setInvalidLinks] = useState([]);

  // Check if URL limit is reached
  const isUrlLimitReached = crawlUrlLimit && crawledUrlCount >= crawlUrlLimit;

  const handleClose = () => {
    if (!loading) {
      setLinks([]);
      setError(null);
      setLinkCountError(false);
      setInvalidLinks([]);
      onHide();
    }
  };

  const handleAddLinks = async () => {
    if (linkCountError) return; // Prevent submission if too many links
    try {
      setLoading(true);
      setError(null);
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        links: links,
      });

      const response = await fetch(`${apiUrl}/api/v1/train/add-links`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        let errorDetail = t('dashboard.training.modals.addlinksmodal.errorFailed');
        try {
          const errorData = await response.json();
          if (errorData.message) {
            // Map backend error codes/messages to frontend messages
            if (errorData.message === 'Too many links') {
              errorDetail = t('dashboard.training.modals.addlinksmodal.errorTooManyLinks', { maxLinks: MAX_LINKS });
            } else if (errorData.message === 'Website already scanned') {
              errorDetail = t('dashboard.training.modals.addlinksmodal.errorAlreadyScanned');
            } else {
              errorDetail = errorData.detail;
            }
          }
        } catch (jsonError) {
          // Ignore if response is not JSON
        }
        throw new Error(errorDetail);
      }

      handleClose();
      await onPollState();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Enhanced URL validation
  const isValidUrl = (url) => {
    try {
      const trimmedUrl = url.trim();
      if (!trimmedUrl) return false;

      // Add https:// protocol if not provided
      const urlToCheck = /^https?:\/\//i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
      const parsedUrl = new URL(urlToCheck);

      // Validate hostname has proper domain structure
      const hostname = parsedUrl.hostname;

      // Domain must contain at least one dot and have a valid TLD
      if (!hostname.includes('.')) return false;

      // Validate domain format: no invalid characters, proper structure
      const domainPattern = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?)*\.[a-zA-Z]{2,}$/;
      if (!domainPattern.test(hostname)) return false;

      return true;
    } catch (e) {
      return false;
    }
  };

  const handleLinksChange = (e) => {
    const input = e.target.value;
    const linksArray = input
      .split(/[\n,]+/) // Split by new lines or commas
      .map((link) => link.trim()) // Trim whitespace
      .filter((link) => link); // Remove empty entries

    // Validate each link
    const invalid = linksArray.filter((link) => !isValidUrl(link));
    setInvalidLinks(invalid);

    setLinks(linksArray);
    setLinkCountError(linksArray.length > MAX_LINKS);
    if (linksArray.length <= MAX_LINKS && error && error.includes(t('dashboard.training.modals.addlinksmodal.errorTooManyLinks', { maxLinks: MAX_LINKS }))) {
      setError(null); // Clear the specific error if count is now valid
    }
  };

  // Effect to handle clearing generic errors when link count becomes valid
  useEffect(() => {
    if (!linkCountError && error && error === t('dashboard.training.modals.addlinksmodal.errorTooManyLinks', { maxLinks: MAX_LINKS })) {
      setError(null);
    }
  }, [linkCountError, error, t]);


  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
      centered
      size="lg"
      className="training-display-container"
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>{t('dashboard.training.modals.addlinksmodal.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(error || linkCountError) && (
          <Alert variant='danger' onClose={() => { setError(null); setLinkCountError(false); }} dismissible>
            {linkCountError ? t('dashboard.training.modals.addlinksmodal.errorTooManyLinks', { maxLinks: MAX_LINKS }) : error}
          </Alert>
        )}

        {isUrlLimitReached && (
          <CompactUpgradePrompt
            message={t('dashboard.training.modals.addlinksmodal.upgradeMessage')}
            buttonText={t('dashboard.training.modals.addlinksmodal.upgradeButton')}
            type="quota"
            percentage={100}
          />
        )}

        <Form>
          <Form.Group className='mb-4'>
            <Form.Label>{t('dashboard.training.modals.addlinksmodal.label')}</Form.Label>
            <Form.Control
              as='textarea'
              rows={6}
              placeholder={t('dashboard.training.modals.addlinksmodal.placeholder')}
              onChange={handleLinksChange}
              disabled={loading || isUrlLimitReached}
              isInvalid={linkCountError}
            />
            <Form.Text className={linkCountError ? 'text-danger' : ''}>
              {t('dashboard.training.modals.addlinksmodal.helpText', { maxLinks: MAX_LINKS })}
            </Form.Text>
          </Form.Group>
          {links.length > 0 && (
            <div className="selected-items-list">
              <p>{t('dashboard.training.modals.addlinksmodal.linksToAddLabel')} <span className={`badge ${linkCountError ? 'bg-danger' : 'bg-primary'}`}>{links.length}</span></p>
              <ul>
                {links.map((link, index) => (
                  <li key={index} className={invalidLinks.includes(link) ? 'text-danger' : ''}>{link}</li>
                ))}
              </ul>
            </div>
          )}
          {invalidLinks.length > 0 && (
            <Alert variant="warning">
              {t('dashboard.training.modals.addlinksmodal.invalidUrlsWarning', { count: invalidLinks.length })}
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton className='cancel-btn' onClick={handleClose} disabled={loading}>
          {t('dashboard.training.modals.addlinksmodal.cancelButton')}
        </CancelButton>
        <PrimaryButton
          className='primary-btn'
          onClick={handleAddLinks}
          disabled={links.length === 0 || loading || linkCountError || invalidLinks.length > 0 || isUrlLimitReached}
        >
          {loading ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                aria-hidden='true'
                className='me-2'
              />
              {t('dashboard.training.modals.addlinksmodal.addingButton')}
            </>
          ) : (
            t('dashboard.training.modals.addlinksmodal.addButton')
          )}
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
};

export default AddLinksModal;
